import {
  caseWildcardPubnubChannel,
  isIntakeSplitCompleteEvent,
  isIntakeStatusUpdateEvent,
  isSegmentStatusUpdateEvent,
} from "Backend";
import { MessageEvent } from "pubnub";
import { usePubNub } from "pubnub-react";
import { useEffect, useMemo } from "react";
import { useAppDispatch } from "redux/hooks";
import { DocumentStatus } from "generated/graphql";
import { fetchCaseSilent, handleSegmentStatusUpdate, setIntakeSegmentCount, setIntakeStatus } from "redux/slices/case";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";

interface DocumentEventsConsumerProps {
  caseId: number;
  children: React.ReactNode;
}

const DocumentEventsConsumer = (props: DocumentEventsConsumerProps) => {
  const { caseId } = props;
  const pubnub = usePubNub();
  const caseChannel = caseWildcardPubnubChannel(caseId);
  const dispatch = useAppDispatch();
  const snackpatch = useAppDispatchWithSnackbar();

  const listener = useMemo(
    () => ({
      message: (evt: MessageEvent) => {
        const payload = JSON.parse(evt.message);
        if (isSegmentStatusUpdateEvent(payload)) {
          void snackpatch(handleSegmentStatusUpdate(payload), {
            errorMessage: "Failed to handle segment status update",
          });
        } else if (isIntakeStatusUpdateEvent(payload)) {
          dispatch(
            setIntakeStatus({
              intakeId: payload.documentIntakeId,
              status: payload.status,
              segmentMetrics: payload.segmentMetrics,
            })
          );
          if (payload.status === DocumentStatus.PendingReview) {
            void snackpatch(fetchCaseSilent({ caseId: caseId }), {
              errorMessage: "Failed to load case",
            });
          }
        } else if (isIntakeSplitCompleteEvent(payload)) {
          dispatch(
            setIntakeSegmentCount({
              intakeId: payload.documentIntakeId,
              segmentCount: payload.segmentCount,
            })
          );
        }
      },
    }),
    [caseId, dispatch]
  );

  useEffect(() => {
    pubnub.addListener(listener);
    pubnub.subscribe({ channels: [caseChannel] });

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [caseChannel] });
    };
  }, [pubnub, caseChannel]);

  return <>{props.children}</>;
};

export default DocumentEventsConsumer;
